/**
 * Demo / store-review data helpers (admin panel only).
 *
 * The backend seeds a small set of reviewer accounts, orders and GPS trails so
 * App Store / Google Play reviewers can exercise the driver app. Real drivers
 * never see that data, but admin does — so admin surfaces mark it with a badge
 * instead of hiding it, and offer an opt-in filter to take it out of the view.
 *
 * The backend stamps `is_demo` on admin responses only, so this flag never
 * reaches the driver app, the client dashboard, or public tracking.
 */

const STORAGE_KEY = 'admin_show_demo_data';
const CHANGE_EVENT = 'admin-show-demo-data-changed';

/** True when a record came from the store-review seed. */
export function isDemoRecord(record) {
  return Boolean(record && record.is_demo);
}

/** Read the persisted preference. Demo data is shown (badged) by default. */
export function getShowDemoData() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

/** Persist the preference and notify every mounted admin surface. */
export function setShowDemoData(show) {
  try {
    window.localStorage.setItem(STORAGE_KEY, show ? 'true' : 'false');
  } catch {
    /* private mode — keep the in-memory value only */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: Boolean(show) }));
}

/** Subscribe to preference changes. Returns an unsubscribe function. */
export function subscribeShowDemoData(listener) {
  const onChange = () => listener(getShowDemoData());
  window.addEventListener(CHANGE_EVENT, onChange);
  // Keep other browser tabs of the same panel in sync.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * Drop demo rows when the admin has switched them off.
 * Always returns a new array only when filtering, so referential equality is
 * preserved for the common "show everything" case.
 */
export function applyDemoFilter(records, showDemoData) {
  if (showDemoData) return records;
  if (!Array.isArray(records)) return records;
  return records.filter((record) => !isDemoRecord(record));
}

export default {
  isDemoRecord,
  getShowDemoData,
  setShowDemoData,
  subscribeShowDemoData,
  applyDemoFilter,
};
